/**
 * F7 · The week, on one screen, for four people.
 *
 * *"Who is where, school runs, sport, who fetches whom. Read from the family
 * calendar. Lifts in bold."*
 *
 * ## There is no family calendar
 *
 * Checked rather than assumed, three ways, because this is the one fact the whole
 * feature turns on:
 *
 *  · `fabric-api` has no calendar route. Its route table runs from `/attention` to
 *    `/waste` and there is nothing between them that reads a calendar.
 *  · Fabric holds one Google credential and its scope list is exactly
 *    `["https://www.googleapis.com/auth/cloud-platform"]`. That is a deployment
 *    identity. It cannot read a calendar and should not be given the scope to.
 *  · Home Assistant does have calendar entities, and Fabric's config route names
 *    them in its *"not here"* list on purpose.
 *
 * So the honest render is a **week with no entries in it**: seven real dates, four
 * real people, and an em dash in every cell. Not one plausible school run.
 *
 * That is not a placeholder screen — it is the feature, minus its source. The four
 * names are real, the dates are real, today is marked, and the deadlines the estate
 * genuinely knows about (Vault expiries, Waypoint bookings — anything on the
 * attention feed carrying a date) are placed on the correct day. What is missing is
 * named in one sentence with the next action beside it. The day a credential exists,
 * `entries` is assigned from a loader and every cell fills; nothing else changes.
 *
 * ## Why an invented event would be the worst possible failure here
 *
 * A wrong temperature is a wrong temperature. A plausible "Liam · cricket · 15:30 ·
 * Mandri fetches" is an instruction, and a household that acts on it once and finds
 * nobody at the field will never trust the screen again — and will be right not to.
 * A blank week is useless; a confident wrong week is expensive.
 */
import type { Attention } from "./estate.svelte";
import { addDays, dayOfWeek, houseISO, mondayOf } from "./housetime";
import type { SourceState, Unwired } from "./sources";

/**
 * The four people, in household order rather than alphabetical.
 *
 * Hard-coded, and that is the right call rather than a shortcut: there is no
 * household-members route either (`GET /users` is the access list — accounts, not
 * people, and the boys do not have accounts). Four names in a file that changes
 * when the family changes is honest; four names invented from a `/users` response
 * would put a colleague's Google account in a school-run grid.
 *
 * `short` is what a narrow column shows. Initials were tried and rejected: two of
 * the four are the parents, and a grid of C/M/L/E asks the reader to decode rather
 * than glance.
 */
export const PEOPLE = [
  { id: "christo", name: "Christo", short: "Christo" },
  { id: "mandri", name: "Mandri", short: "Mandri" },
  { id: "liam", name: "Liam", short: "Liam" },
  { id: "eben", name: "Eben", short: "Eben" },
] as const;

export type PersonId = (typeof PEOPLE)[number]["id"];

/**
 * One thing on one person's day.
 *
 * `lift` is a field rather than a category because *"who fetches whom"* is not a
 * kind of event — it is a property some events have. Cricket practice is a fixture;
 * the fact that Mandri fetches afterwards is the part that needs a driver, and it
 * is what F7 asks to be rendered in bold. Modelling it as a separate event type
 * would put the practice and the lift on two rows and hide the connection between
 * them, which is the connection the whole screen exists to show.
 */
export type WeekEntry = {
  id: string;
  /** Whose row this sits on. */
  personId: PersonId;
  /** House ISO date. */
  date: string;
  /** "07:10", or null for something that has no clock time. */
  at: string | null;
  what: string;
  where: string | null;
  /** Set only when somebody has to drive. `by` is the driver; `who` is carried. */
  lift: { by: PersonId | null; who: PersonId[] } | null;
  /** Which calendar it came from, once there is one. */
  source: string;
};

/**
 * A dated thing the estate already knows about, placed on the week.
 *
 * These come from the attention feed, which is real and wired — a Vault licence
 * expiring on Thursday, a Waypoint booking starting Friday. They are deliberately
 * **not** `WeekEntry`s and are not drawn in the people grid: a deadline is not an
 * appointment and nobody is expected to be anywhere. Rendering them as calendar
 * entries would be inventing events out of real data, which is the same lie one
 * step removed.
 */
export type WeekMark = {
  id: string;
  date: string;
  text: string;
  source: string;
  overdue: boolean;
  urgent: boolean;
  deepLink: string;
};

/** What the week cannot show, and what would fix it. */
export const CALENDAR: Unwired = {
  title: "The week",
  why: "No family calendar is connected. Fabric serves no calendar route and holds no calendar credential.",
  next: "Give Fabric a calendar scope and a GET /calendar/week; these cells fill themselves.",
};

class Week {
  /**
   * Declared, not discovered.
   *
   * A runtime `state` would have to start at `loading` and settle — which means a
   * spinner, then an empty week, which reads as *the calendar is empty this week*.
   * It is not empty; it does not exist. `unwired` is a fact about the estate that
   * is known at build time, and saying so costs one field.
   *
   * Typed as the full union rather than the literal so the day a loader exists it
   * can assign `"loading"` here without this line having to change.
   */
  state = $state<SourceState>("unwired");

  /**
   * Always empty today. `$state` so that a loader is a two-line addition rather
   * than a rewrite — the components below already render entries correctly and
   * have no way to tell that none have ever arrived.
   */
  entries = $state<WeekEntry[]>([]);

  /**
   * Which seven days to draw, and what to call them.
   *
   * F11 is the reason this takes `now`: on a Sunday the household is not looking at
   * the week it is finishing, it is looking at the one starting tomorrow. Every
   * other day it is Monday-to-Sunday of the week you are standing in, because a
   * rolling seven days makes "Thursday" a different column each morning and the
   * whole value of a wall week is that it stops moving.
   */
  range(now: Date | number = Date.now()): { from: string; label: string; ahead: boolean } {
    const today = houseISO(now);
    if (dayOfWeek(today) === 0) {
      return { from: addDays(today, 1), label: "The week ahead", ahead: true };
    }
    return { from: mondayOf(today), label: "This week", ahead: false };
  }

  /** The seven house dates of a week starting at `from`. */
  days(from: string): string[] {
    return Array.from({ length: 7 }, (_, i) => addDays(from, i));
  }

  /** One person's entries for one day, earliest first, all-day things last. */
  entriesFor(personId: PersonId, date: string): WeekEntry[] {
    return this.entries
      .filter((e) => e.personId === personId && e.date === date)
      .sort((a, b) => (a.at ?? "99:99").localeCompare(b.at ?? "99:99"));
  }
}

export const week = new Week();

/**
 * The dated items on the attention feed that fall inside this week.
 *
 * The only real data F7 has today, and it is worth having: *"what is expiring and
 * what is booked"* is half of what `03-shell-and-layout.md` says a partner's home
 * screen leads with, and both of those arrive on the attention feed with a `dueAt`.
 *
 * Undated items are excluded rather than piled onto today. An item with no deadline
 * is not due today; it has no day, and putting it on one would be a guess with a
 * date attached.
 */
export function deadlinesIn(items: Attention[], from: string): WeekMark[] {
  const days = new Set(Array.from({ length: 7 }, (_, i) => addDays(from, i)));
  return items
    .filter((a) => a.dueAt !== null)
    .map((a) => ({
      id: a.id,
      date: houseISO(new Date(a.dueAt as string)),
      text: a.text,
      source: a.source,
      overdue: a.overdue,
      urgent: a.severity === "urgent",
      deepLink: a.deepLink,
    }))
    .filter((m) => days.has(m.date))
    .sort((a, b) => a.date.localeCompare(b.date));
}
